import React, {createContext, useCallback, useContext, useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {useSelector} from 'react-redux';
import * as gridLayoutApi from '../utils/gridLayoutApi';
import {
  readAllGridLayouts,
  saveGridLayout as cacheLayout,
  removeGridLayout as removeCachedLayout,
} from '../utils/gridLayoutStorage';

/**
 * Holds every grid column layout for the signed in user.
 *
 * Loaded once per session from `/gridLayouts` so the layouts follow the user to
 * any browser or machine, seeded meanwhile from the browser cache so a reload
 * does not flash the default column order. Writes go to both: the cache
 * immediately, the server debounced, since a single drag fires several column
 * events.
 */

// A drag emits a burst of column events, and a user nudging columns into place
// emits several drags. One write once they settle is enough.
const SAVE_DEBOUNCE_MS = 1000;

export const GridLayoutContext = createContext(undefined);

export const GridLayoutContextProvider = (props)=> {
  const userId = useSelector((state)=> state.user.id);
  const isAuthenticated = useSelector((state)=> state.user.isAuthenticated);

  // gridKey -> AG Grid column state array.
  const [layouts, setLayouts] = useState({});
  const [layoutsLoaded, setLayoutsLoaded] = useState(false);
  const pendingSaves = useRef({});

  const flushPendingSaves = useCallback(()=> {
    Object.values(pendingSaves.current).forEach(clearTimeout);
    pendingSaves.current = {};
  }, []);

  useEffect(()=> {
    if(!isAuthenticated || !userId) {
      flushPendingSaves();
      setLayouts({});
      setLayoutsLoaded(false);
      return undefined;
    }

    // Paint from the cache first, then let the server's copy win.
    setLayouts(readAllGridLayouts(userId));

    let cancelled = false;
    gridLayoutApi.fetchGridLayouts().then((serverLayouts)=> {
      if(cancelled) return;

      if(serverLayouts) {
        const next = {};
        serverLayouts.forEach((layout)=> {
          try {
            const columnState = JSON.parse(layout.columnState);
            if(!columnState) return;

            next[layout.gridKey] = columnState;
            cacheLayout(userId, layout.gridKey, columnState);
          } catch(e) {
            // A layout we cannot read is one grid falling back to its default
            // columns, not a reason to drop the rest.
          }
        });
        setLayouts(next);
      }

      setLayoutsLoaded(true);
    });

    return ()=> {
      cancelled = true;
    };
  }, [isAuthenticated, userId, flushPendingSaves]);

  // Timers outlive the component if the user navigates away mid-debounce.
  useEffect(()=> flushPendingSaves, [flushPendingSaves]);

  const saveLayout = useCallback((gridKey, columnState)=> {
    if(!gridKey || !columnState) return;

    setLayouts((current)=> ({...current, [gridKey]: columnState}));
    cacheLayout(userId, gridKey, columnState);

    clearTimeout(pendingSaves.current[gridKey]);
    pendingSaves.current[gridKey] = setTimeout(()=> {
      delete pendingSaves.current[gridKey];
      gridLayoutApi.saveGridLayout(gridKey, columnState);
    }, SAVE_DEBOUNCE_MS);
  }, [userId]);

  const removeLayout = useCallback((gridKey)=> {
    if(!gridKey) return;

    clearTimeout(pendingSaves.current[gridKey]);
    delete pendingSaves.current[gridKey];

    setLayouts((current)=> {
      const next = {...current};
      delete next[gridKey];
      return next;
    });
    removeCachedLayout(userId, gridKey);
    gridLayoutApi.deleteGridLayout(gridKey);
  }, [userId]);

  const context = {
    layouts: layouts,
    layoutsLoaded: layoutsLoaded,
    saveLayout: saveLayout,
    removeLayout: removeLayout,
  };

  return (
    <GridLayoutContext.Provider value={context}>
      {props.children}
    </GridLayoutContext.Provider>
  );
};

GridLayoutContextProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
};

GridLayoutContextProvider.defaultProps = {
  children: null,
};

/**
 * The saved layout for one grid, plus the writers for it. Safe to call outside the
 * provider (tests, storybook-style renders): the grid then keeps its default
 * columns and saving is a no-op.
 */
export const useGridLayout = (gridKey)=> {
  const context = useContext(GridLayoutContext);

  const layout = gridKey && context ? context.layouts[gridKey] : undefined;
  const saveLayout = context?.saveLayout;
  const removeLayout = context?.removeLayout;

  const save = useCallback((columnState)=> {
    if(saveLayout) saveLayout(gridKey, columnState);
  }, [gridKey, saveLayout]);

  const remove = useCallback((keyToRemove)=> {
    if(removeLayout) removeLayout(keyToRemove || gridKey);
  }, [gridKey, removeLayout]);

  return {
    layout: layout || null,
    layoutsLoaded: context ? context.layoutsLoaded : false,
    saveLayout: save,
    removeLayout: remove,
  };
};
